import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { OfflineBanner } from './components/OfflineBanner';
import { CompareProvider } from './context/CompareContext';
import { CompareFloatingBar } from './components/CompareFloatingBar';
import { PropertyAlertProvider } from './context/PropertyAlertContext';
import { MobileBottomNav } from './components/MobileBottomNav';
import { InstallAppBanner } from './components/InstallAppBanner';

const CompareModal = React.lazy(() =>
  import('./components/CompareModal').then((m) => ({ default: m.CompareModal }))
);
const PropertyAlertModal = React.lazy(() =>
  import('./components/PropertyAlertModal').then((m) => ({ default: m.PropertyAlertModal }))
);

// Critical Entry Page (Eagerly Loaded for Immediate First Contentful Paint)
import { HomePage } from './pages/HomePage';

// Lazy Loaded Routes (Code-Splitting for High Performance on 3G/4G Networks)
const BuyPage = React.lazy(() =>
  import('./pages/BuyPage').then((m) => ({ default: m.BuyPage }))
);
const RentPage = React.lazy(() =>
  import('./pages/RentPage').then((m) => ({ default: m.RentPage }))
);
const StayPage = React.lazy(() =>
  import('./pages/StayPage').then((m) => ({ default: m.StayPage }))
);
const PropertyDetailPage = React.lazy(() =>
  import('./pages/PropertyDetailPage').then((m) => ({ default: m.PropertyDetailPage }))
);
const HotelDetailPage = React.lazy(() =>
  import('./pages/HotelDetailPage').then((m) => ({ default: m.HotelDetailPage }))
);
const PostPropertyPage = React.lazy(() =>
  import('./pages/PostPropertyPage').then((m) => ({ default: m.PostPropertyPage }))
);
const VerifyPropertyPage = React.lazy(() =>
  import('./pages/VerifyPropertyPage').then((m) => ({ default: m.VerifyPropertyPage }))
);
const VerifyTitreFoncierGuidePage = React.lazy(() =>
  import('./pages/VerifyTitreFoncierGuidePage').then((m) => ({ default: m.VerifyTitreFoncierGuidePage }))
);
const BuyingCostsGuidePage = React.lazy(() =>
  import('./pages/BuyingCostsGuidePage').then((m) => ({ default: m.BuyingCostsGuidePage }))
);
const CustomaryVsTitreFoncierGuidePage = React.lazy(() =>
  import('./pages/CustomaryVsTitreFoncierGuidePage').then((m) => ({ default: m.CustomaryVsTitreFoncierGuidePage }))
);
const FindSurveyorNotaryGuidePage = React.lazy(() =>
  import('./pages/FindSurveyorNotaryGuidePage').then((m) => ({ default: m.FindSurveyorNotaryGuidePage }))
);
const MarketplaceCategoryLocationPage = React.lazy(() =>
  import('./pages/MarketplaceCategoryLocationPage').then((m) => ({ default: m.MarketplaceCategoryLocationPage }))
);
const CameroonCitiesPage = React.lazy(() =>
  import('./pages/CameroonCitiesPage').then((m) => ({ default: m.CameroonCitiesPage }))
);
const RegionDetailPage = React.lazy(() =>
  import('./pages/RegionDetailPage').then((m) => ({ default: m.RegionDetailPage }))
);
const MapPage = React.lazy(() =>
  import('./pages/MapPage').then((m) => ({ default: m.MapPage }))
);
const ProfessionalsPage = React.lazy(() =>
  import('./pages/ProfessionalsPage').then((m) => ({ default: m.ProfessionalsPage }))
);
const SavedPropertiesPage = React.lazy(() =>
  import('./pages/SavedPropertiesPage').then((m) => ({ default: m.SavedPropertiesPage }))
);
const ResourcesPage = React.lazy(() =>
  import('./pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage }))
);
const AboutPage = React.lazy(() =>
  import('./pages/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const NewsPage = React.lazy(() =>
  import('./pages/NewsPage').then((m) => ({ default: m.NewsPage }))
);
const FaqPage = React.lazy(() =>
  import('./pages/FaqPage').then((m) => ({ default: m.FaqPage }))
);
const ContactPage = React.lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const TermsPage = React.lazy(() =>
  import('./pages/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = React.lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const SignInPage = React.lazy(() =>
  import('./pages/SignInPage').then((m) => ({ default: m.SignInPage }))
);
const SignUpPage = React.lazy(() =>
  import('./pages/SignUpPage').then((m) => ({ default: m.SignUpPage }))
);
const ForgotPasswordPage = React.lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = React.lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const AuthActionPage = React.lazy(() =>
  import('./pages/AuthActionPage').then((m) => ({ default: m.AuthActionPage }))
);
const ProfilePage = React.lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const ManageListingsPage = React.lazy(() =>
  import('./pages/ManageListingsPage').then((m) => ({ default: m.ManageListingsPage }))
);
const UserAccountDashboardPage = React.lazy(() =>
  import('./pages/UserAccountDashboardPage').then((m) => ({ default: m.UserAccountDashboardPage }))
);
const AdminPlatformPage = React.lazy(() =>
  import('./pages/admin/AdminPlatformPage').then((m) => ({ default: m.AdminPlatformPage }))
);
const AdminLoginPageNew = React.lazy(() =>
  import('./pages/admin/AdminLoginPageNew').then((m) => ({ default: m.AdminLoginPageNew }))
);
const NotFoundPage = React.lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const RenewListingPage = React.lazy(() =>
  import('./pages/RenewListingPage').then((m) => ({ default: m.RenewListingPage }))
);
const SitemapPage = React.lazy(() =>
  import('./pages/SitemapPage').then((m) => ({ default: m.SitemapPage }))
);
const AdminProtectedRoute = React.lazy(() =>
  import('./components/admin/AdminProtectedRoute').then((m) => ({ default: m.AdminProtectedRoute }))
);
import { ErrorBoundary } from './components/ErrorBoundary';
import { EmailVerificationGate } from './components/EmailVerificationGate';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { useLanguage } from './context/LanguageContext';
import { getLegacyRedirect, getLocalizedEquivalentUrl } from './lib/routes';
import { toSafeIsoString } from './lib/dateUtils';

const SafeMapPage: React.FC<{ properties: Property[]; isLoading: boolean }> = (props) => (
  <ErrorBoundary
    fallbackTitle="Carte Cadastrale / Cadastral Map Notice"
    fallbackSubtitle="Le module cartographique a rencontré une brève interruption. Vos autres fonctionnalités restent actives."
  >
    <MapPage {...props} />
  </ErrorBoundary>
);

const SafePropertyDetailPage: React.FC<{
  properties: Property[];
  onDeleteProperty: (id: string) => Promise<void>;
  onUpdateProperty: (id: string, updated: Partial<Property>) => Promise<void> | void;
}> = (props) => (
  <ErrorBoundary
    fallbackTitle="Détails de l'Annonce / Property Details Notice"
    fallbackSubtitle="Les détails de cette annonce sont temporairement indisponibles. Cliquez sur Recharger pour actualiser."
  >
    <PropertyDetailPage {...props} />
  </ErrorBoundary>
);

const SafePostPropertyPage: React.FC<{
  properties: Property[];
  onPropertyCreated: (p: Property) => void;
}> = (props) => (
  <ErrorBoundary
    fallbackTitle="Publication d'Annonce / Post Property Notice"
    fallbackSubtitle="Le formulaire de publication a rencontré une brève interruption. Vos données ont été sauvegardées."
  >
    <PostPropertyPage {...props} />
  </ErrorBoundary>
);

const RedirectToFrenchRoute: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const target = getLocalizedEquivalentUrl(location.pathname, 'fr', searchParams.toString());
  return <Navigate to={target} replace />;
};

const LegacyRedirectHandler: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { language } = useLanguage();

  // Redirect root '/' to localized home '/en' or '/fr' to maintain clean URL locale prefix
  if (location.pathname === '/' || location.pathname === '') {
    const target = language === 'fr' ? '/fr' : '/en';
    const finalTarget = searchParams.toString() ? `${target}?${searchParams.toString()}` : target;
    return <Navigate to={finalTarget} replace />;
  }

  const redirect = getLegacyRedirect(location.pathname, searchParams, language);
  if (redirect) {
    const target = redirect.search ? `${redirect.destination}?${redirect.search}` : redirect.destination;
    return <Navigate to={target} replace />;
  }
  return null;
};

import { Property, PropertyInquiry } from './types';
import { CheckCircle2, X } from 'lucide-react';
import { isDemoProperty } from './lib/demoPropertyHelper';
import { useAuth } from './context/AuthContext';

import { useWebmasterVerification } from './lib/useWebmasterVerification';

const RouteLoadingFallback = () => null;

function AppContent() {
  useWebmasterVerification();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  // Explicit route language synchronization (/fr/ vs /en/) on pathname transitions
  React.useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path === '/fr' || path.startsWith('/fr/')) {
      setLanguage('fr');
    } else if (path === '/en' || path.startsWith('/en/')) {
      setLanguage('en');
    }
  }, [location.pathname]);

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/clm-master-vault-9820');
  const { user, userProfile, isAuthenticated, isSuperAdmin, signOut: authSignOut, loading: authLoading } = useAuth();
  const [propertiesList, setPropertiesList] = useState<Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState<boolean>(true);
  const [inquiriesList, setInquiriesList] = useState<PropertyInquiry[]>([]);
  const [currentUser, setCurrentUser] = useState<{ name: string; email: string; role: string } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Restore cached properties & user profile post-mount to ensure first render exactly matches server HTML
  React.useEffect(() => {
    try {
      const cached = localStorage.getItem('clm_cached_active_properties');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Normalize any stale cached properties so dates are guaranteed strings and never throw in .localeCompare or .sort
          const sanitized = parsed.map((p: any) => ({
            ...p,
            createdAt: toSafeIsoString(p?.createdAt || p?.created_at, new Date().toISOString()),
            updatedAt: toSafeIsoString(p?.updatedAt || p?.updated_at) || undefined,
            publishedAt: toSafeIsoString(p?.publishedAt || p?.published_at) || null,
          }));
          setPropertiesList(sanitized);
          setIsLoadingProperties(false);
        }
      }
      const rawUser = localStorage.getItem('clm_authenticated_user_profile');
      if (rawUser) {
        const p = JSON.parse(rawUser);
        if (p && p.email) {
          setCurrentUser({
            name: (p.fullName && p.fullName.trim()) || p.email.split('@')[0] || 'User',
            email: p.email,
            role: p.role || 'Buyer / Tenant',
          });
        }
      }
    } catch {}
  }, []);

  // Sync currentUser with userProfile and user auth state
  React.useEffect(() => {
    if (userProfile) {
      setCurrentUser({
        name:
          (userProfile.fullName && userProfile.fullName.trim()) ||
          (userProfile.email && userProfile.email.includes('@') ? userProfile.email.split('@')[0] : '') ||
          'User',
        email: userProfile.email || '',
        role: userProfile.role || 'Buyer / Tenant',
      });
    } else if (user) {
      setCurrentUser({
        name:
          user.displayName ||
          (user.email && user.email.includes('@') ? user.email.split('@')[0] : '') ||
          'User',
        email: user.email || '',
        role: 'Buyer / Tenant',
      });
    } else if (!authLoading && !isAuthenticated) {
      setCurrentUser(null);
    }
  }, [userProfile, user, isAuthenticated, authLoading]);

  // Real-time synchronization with Cloud Firestore properties (deferred to idle to avoid network contention on initial load)
  React.useEffect(() => {
    let unsubscribe = () => {};
    let timerId: any = null;

    const startSync = () => {
      import('./lib/firestoreService').then(({ subscribeToProperties, mapFirebasePropertyToAppProperty }) => {
        unsubscribe = subscribeToProperties((remoteProps) => {
          setIsLoadingProperties(false);
          if (remoteProps && remoteProps.length > 0) {
            const mapped: Property[] = remoteProps
              .filter((rp) => !isDemoProperty(rp))
              .map(mapFirebasePropertyToAppProperty);
            setPropertiesList(mapped);
            try {
              localStorage.setItem('clm_cached_active_properties', JSON.stringify(mapped));
            } catch {}
          } else {
            setPropertiesList([]);
          }
        });
      }).catch((err) => {
        console.warn('Deferred firestore property subscription note:', err);
        setIsLoadingProperties(false);
      });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const handle = (window as any).requestIdleCallback(startSync, { timeout: 2500 });
      return () => {
        (window as any).cancelIdleCallback(handle);
        unsubscribe();
      };
    } else {
      timerId = setTimeout(startSync, 1200);
      return () => {
        if (timerId) clearTimeout(timerId);
        unsubscribe();
      };
    }
  }, []);

  // Real-time synchronization of inquiries for both Admins and normal Users (Sellers & Buyers)
  const prevInquiriesCountRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!isAuthenticated || !user) {
      setInquiriesList([]);
      prevInquiriesCountRef.current = null;
      return;
    }

    const isAdmin =
      isSuperAdmin ||
      userProfile?.role === 'Admin' ||
      currentUser?.role === 'Admin';

    let unsubscribe: () => void = () => {};

    if (isAdmin) {
      import('./lib/firestoreService').then(({ cleanupDemoPropertiesFromFirestore, subscribeToAllInquiries }) => {
        cleanupDemoPropertiesFromFirestore().catch(() => {});
        unsubscribe = subscribeToAllInquiries((inqs) => {
          if (prevInquiriesCountRef.current !== null && inqs.length > prevInquiriesCountRef.current) {
            showNotification('Nouveau lead acheteur reçu ! / New buyer inquiry received!');
          }
          prevInquiriesCountRef.current = inqs.length;
          setInquiriesList(inqs);
        });
      }).catch(() => {});
    } else {
      import('./lib/firestoreService').then(({ subscribeToInquiriesForUser }) => {
        unsubscribe = subscribeToInquiriesForUser(user.uid, user.email || userProfile?.email, (inqs) => {
          if (prevInquiriesCountRef.current !== null && inqs.length > prevInquiriesCountRef.current) {
            showNotification('Nouvelle demande reçue ! / New inquiry received!');
          }
          prevInquiriesCountRef.current = inqs.length;
          setInquiriesList(inqs);
        });
      }).catch(() => {});
    }

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, user?.uid, user?.email, isSuperAdmin, userProfile?.role, userProfile?.email, currentUser?.role]);

  const showNotification = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const handlePropertyCreated = (newProp: Property) => {
    setPropertiesList((prev) => {
      const exists = prev.some((p) => p.id === newProp.id);
      if (exists) {
        return prev.map((p) => (p.id === newProp.id ? newProp : p));
      }
      return [newProp, ...prev];
    });
    showNotification(`Success! Property "${newProp.title}" saved to backend and active listings.`);
  };

  const handleUpdateProperty = async (id: string, updated: Partial<Property>) => {
    const existing = propertiesList.find((p) => p.id === id);

    if (
      (updated.title && updated.title !== existing?.title) ||
      (updated.description && updated.description !== existing?.description)
    ) {
      try {
        const { translateListingContent } = await import('./lib/translationService');
        const trans = await translateListingContent({
          title: updated.title || existing?.title || '',
          description: updated.description || existing?.description || '',
          existingTitle: existing?.localizedTitle,
          existingDescription: existing?.localizedDescription,
          originalLanguage: existing?.originalLanguage,
        });
        updated.localizedTitle = trans.localizedTitle;
        updated.localizedDescription = trans.localizedDescription;
        updated.originalLanguage = trans.detectedLanguage;
        updated.original_lang = trans.detectedLanguage;
        updated.title_en = trans.localizedTitle.en;
        updated.title_fr = trans.localizedTitle.fr;
        updated.description_en = trans.localizedDescription.en;
        updated.description_fr = trans.localizedDescription.fr;
      } catch (err) {
        console.warn('Auto translation update note:', err);
      }
    }

    setPropertiesList((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...updated } : p))
    );
    try {
      const firestoreUpdates: any = {
        title: updated.title,
        status: updated.status,
      };
      if (updated.description !== undefined) firestoreUpdates.description = updated.description;
      if (updated.title_en !== undefined) firestoreUpdates.title_en = updated.title_en;
      if (updated.title_fr !== undefined) firestoreUpdates.title_fr = updated.title_fr;
      if (updated.description_en !== undefined) firestoreUpdates.description_en = updated.description_en;
      if (updated.description_fr !== undefined) firestoreUpdates.description_fr = updated.description_fr;
      if (updated.original_lang !== undefined) firestoreUpdates.original_lang = updated.original_lang;
      if (updated.localizedTitle !== undefined) firestoreUpdates.localized_title = updated.localizedTitle;
      if (updated.localizedDescription !== undefined) firestoreUpdates.localized_description = updated.localizedDescription;
      if (updated.originalLanguage !== undefined) {
        firestoreUpdates.original_language = updated.originalLanguage;
        if (!firestoreUpdates.original_lang) firestoreUpdates.original_lang = updated.originalLanguage;
      }
      if (updated.priceRaw !== undefined) firestoreUpdates.price = updated.priceRaw;
      if (updated.location) {
        const parts = updated.location.split(',');
        if (parts[0]) firestoreUpdates.city = parts[0].trim();
        if (parts[1]) firestoreUpdates.neighborhood = parts[1].trim();
      }
      if (updated.type) firestoreUpdates.property_type = updated.type;
      if (updated.featured !== undefined) firestoreUpdates.featured = updated.featured;
      if (updated.boostPlan !== undefined) firestoreUpdates.boost_plan = updated.boostPlan;
      if (updated.boostExpiresAt !== undefined) firestoreUpdates.boost_expires_at = updated.boostExpiresAt;
      if (updated.boostedAt !== undefined) firestoreUpdates.boosted_at = updated.boostedAt;
      if (updated.boostTransactionId !== undefined) firestoreUpdates.boost_transaction_id = updated.boostTransactionId;
      if (updated.boostOperator !== undefined) firestoreUpdates.boost_operator = updated.boostOperator;
      if (updated.boundaryCoordinates !== undefined) firestoreUpdates.boundary_coordinates = updated.boundaryCoordinates;
      if (updated.cadastralBeacons !== undefined) firestoreUpdates.cadastral_beacons = updated.cadastralBeacons;
      if (updated.calculatedAreaSqm !== undefined) firestoreUpdates.calculated_area_sqm = updated.calculatedAreaSqm;
      if (updated.calculatedPerimeterMeters !== undefined) firestoreUpdates.calculated_perimeter_meters = updated.calculatedPerimeterMeters;
      const { updateProperty: updateFirestoreProperty } = await import('./lib/firestoreService');
      await updateFirestoreProperty(id, firestoreUpdates);
      showNotification('Property updated and saved to backend database.');
    } catch (err) {
      console.warn('Backend update sync note:', err);
      showNotification('Property updated successfully.');
    }
  };

  const handleDeleteProperty = async (id: string) => {
    setPropertiesList((prev) => prev.filter((p) => p.id !== id));
    try {
      const { deleteProperty: deleteFirestoreProperty } = await import('./lib/firestoreService');
      await deleteFirestoreProperty(id);
      showNotification('Property post permanently deleted from backend database.');
    } catch (err) {
      console.warn('Backend delete sync note:', err);
      showNotification('Property removed from your listings.');
    }
  };

  const handleUpdateInquiryStatus = async (id: string, status: 'new' | 'read' | 'contacted') => {
    setInquiriesList((prev) =>
      prev.map((inq) => (inq.id === id ? { ...inq, status } : inq))
    );
    try {
      const { updateInquiryStatusInFirestore } = await import('./lib/firestoreService');
      await updateInquiryStatusInFirestore(id, status);
    } catch (err) {
      console.warn('Sync inquiry status notice:', err);
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    setInquiriesList((prev) => prev.filter((inq) => inq.id !== id));
    try {
      const { deleteInquiryFromFirestore } = await import('./lib/firestoreService');
      await deleteInquiryFromFirestore(id);
      showNotification('Inquiry lead deleted from database.');
    } catch (err) {
      console.warn('Delete inquiry notice:', err);
    }
  };

  const renderCoreRoutes = (prefix = '') => (
    <React.Fragment key={prefix || 'root'}>
      <Route
        path={prefix || '/'}
        element={<HomePage properties={propertiesList} isLoading={isLoadingProperties} onShowToast={showNotification} />}
      />
      <Route
        path={`${prefix}/properties-for-sale`}
        element={<BuyPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/properties-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/land-for-sale`}
        element={<BuyPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/land-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/houses-for-sale`}
        element={<BuyPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/houses-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/apartments-for-sale`}
        element={<BuyPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/apartments-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/commercial-for-sale`}
        element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/commercial-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/shops-and-offices-for-sale`}
        element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/shops-and-offices-for-sale/:citySlug`}
        element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/properties-for-rent`}
        element={<RentPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/properties-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/apartments-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/apartments-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/houses-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/houses-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/studios-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="studio" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/studios-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="studio" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/rooms-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="room" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/rooms-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="room" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/land-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/land-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/commercial-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/commercial-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/shops-and-offices-for-rent`}
        element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/shops-and-offices-for-rent/:citySlug`}
        element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/short-stays`}
        element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/short-stays/:citySlug`}
        element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/hotels`}
        element={<StayPage properties={propertiesList} forcedCategory="hotel" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/hotels/:citySlug`}
        element={<StayPage properties={propertiesList} forcedCategory="hotel" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/motels`}
        element={<StayPage properties={propertiesList} forcedCategory="motel" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/motels/:citySlug`}
        element={<StayPage properties={propertiesList} forcedCategory="motel" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/guesthouses`}
        element={<StayPage properties={propertiesList} forcedCategory="guesthouse" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/guesthouses/:citySlug`}
        element={<StayPage properties={propertiesList} forcedCategory="guesthouse" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/resorts`}
        element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/resorts/:citySlug`}
        element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/listing/:slug`}
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route
        path={`${prefix}/property/:slug`}
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route
        path={`${prefix}/properties/:slug`}
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route
        path={`${prefix}/hotel/:slug`}
        element={<HotelDetailPage properties={propertiesList} />}
      />
      <Route
        path={`${prefix}/short-stay/:slug`}
        element={<HotelDetailPage properties={propertiesList} />}
      />
      <Route
        path={`${prefix}/map`}
        element={<SafeMapPage properties={propertiesList} isLoading={isLoadingProperties} />}
      />
      <Route
        path={`${prefix}/verify-property`}
        element={<VerifyPropertyPage />}
      />
      <Route
        path={`${prefix}/verify-titre-foncier`}
        element={<VerifyTitreFoncierGuidePage />}
      />
      <Route
        path={`${prefix}/blog/how-to-check-titre-foncier-cameroon`}
        element={<VerifyTitreFoncierGuidePage />}
      />
      <Route
        path={`${prefix}/blog/how-to-check-titre-foncier`}
        element={<VerifyTitreFoncierGuidePage />}
      />
      <Route
        path={`${prefix}/verify-land-title`}
        element={<VerifyPropertyPage />}
      />
      <Route
        path={`${prefix}/verify-land`}
        element={<VerifyPropertyPage />}
      />
      <Route
        path={`${prefix}/cities`}
        element={<CameroonCitiesPage />}
      />
      <Route
        path={`${prefix}/cameroon-cities`}
        element={<CameroonCitiesPage />}
      />
      <Route
        path={`${prefix}/cities/:citySlug`}
        element={<CameroonCitiesPage />}
      />
      <Route
        path={`${prefix}/city/:citySlug`}
        element={<CameroonCitiesPage />}
      />
      <Route
        path={`${prefix}/regions`}
        element={<CameroonCitiesPage />}
      />
      <Route
        path={`${prefix}/regions/:regionSlug`}
        element={<RegionDetailPage />}
      />
      <Route
        path={`${prefix}/region/:regionSlug`}
        element={<RegionDetailPage />}
      />
      <Route
        path={`${prefix}/professionals`}
        element={<ProfessionalsPage />}
      />
      <Route
        path={`${prefix}/professionals/:category`}
        element={<ProfessionalsPage />}
      />
      <Route
        path={`${prefix}/saved`}
        element={<SavedPropertiesPage properties={propertiesList} />}
      />
      <Route
        path={`${prefix}/favoris`}
        element={<SavedPropertiesPage properties={propertiesList} />}
      />
      <Route
        path={`${prefix}/agents`}
        element={<ProfessionalsPage />}
      />
      <Route
        path={`${prefix}/resources/cost-of-buying-land-and-taxes`}
        element={<BuyingCostsGuidePage />}
      />
      <Route
        path={`${prefix}/resources/cost-of-buying-land-and-taxes/*`}
        element={<BuyingCostsGuidePage />}
      />
      <Route
        path={`${prefix}/resources/customary-land-vs-titre-foncier`}
        element={<CustomaryVsTitreFoncierGuidePage />}
      />
      <Route
        path={`${prefix}/resources/customary-land-vs-titre-foncier/*`}
        element={<CustomaryVsTitreFoncierGuidePage />}
      />
      <Route
        path={`${prefix}/resources/how-to-find-licensed-surveyor-or-notary`}
        element={<FindSurveyorNotaryGuidePage />}
      />
      <Route
        path={`${prefix}/resources/how-to-find-licensed-surveyor-or-notary/*`}
        element={<FindSurveyorNotaryGuidePage />}
      />
      <Route
        path={`${prefix}/resources`}
        element={<ResourcesPage />}
      />
      <Route
        path={`${prefix}/resources/:topicSlug`}
        element={<ResourcesPage />}
      />
      <Route
        path={`${prefix}/guides`}
        element={<ResourcesPage />}
      />
      <Route
        path={`${prefix}/news`}
        element={<NewsPage />}
      />
      <Route
        path={`${prefix}/news/:slug`}
        element={<NewsPage />}
      />
      <Route
        path={`${prefix}/about`}
        element={<AboutPage />}
      />
      <Route
        path={`${prefix}/faq`}
        element={<FaqPage />}
      />
      <Route
        path={`${prefix}/contact`}
        element={<ContactPage />}
      />
      <Route
        path={`${prefix}/terms`}
        element={<TermsPage />}
      />
      <Route
        path={`${prefix}/privacy`}
        element={<PrivacyPage />}
      />
      <Route
        path={`${prefix}/sitemap`}
        element={<SitemapPage />}
      />
      <Route
        path={`${prefix}/html-sitemap`}
        element={<SitemapPage />}
      />
      <Route
        path={`${prefix}/post-property`}
        element={
          <SafePostPropertyPage
            properties={propertiesList}
            onPropertyCreated={handlePropertyCreated}
          />
        }
      />
      <Route
        path={`${prefix}/signin`}
        element={<SignInPage onLoginSuccess={(user) => setCurrentUser(user)} />}
      />
      <Route
        path={`${prefix}/sign-in`}
        element={<Navigate to={`${prefix || '/en'}/signin`} replace />}
      />
      <Route
        path={`${prefix}/signup`}
        element={<SignUpPage onLoginSuccess={(user) => setCurrentUser(user)} />}
      />
      <Route
        path={`${prefix}/sign-up`}
        element={<Navigate to={`${prefix || '/en'}/signup`} replace />}
      />
      <Route
        path={`${prefix}/forgot-password`}
        element={<ForgotPasswordPage />}
      />
      <Route
        path={`${prefix}/reset-password`}
        element={<ResetPasswordPage />}
      />
      <Route
        path={`${prefix}/auth-action`}
        element={<AuthActionPage />}
      />
      <Route
        path={`${prefix}/profile`}
        element={<ProfilePage />}
      />
      <Route
        path={`${prefix}/renew/:propertyId`}
        element={<RenewListingPage properties={propertiesList} onUpdateProperty={handleUpdateProperty} />}
      />
      <Route
        path={`${prefix}/manage-listings`}
        element={
          <EmailVerificationGate>
            <ManageListingsPage
              properties={propertiesList}
              inquiries={inquiriesList}
              currentUser={currentUser}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onSwitchUser={(u) => setCurrentUser(u)}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/dashboard`}
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/account`}
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/analytics`}
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/seller-analytics`}
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/statistiques`}
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path={`${prefix}/post`}
        element={
          <SafePostPropertyPage
            properties={propertiesList}
            onPropertyCreated={handlePropertyCreated}
          />
        }
      />
      <Route
        path={`${prefix}/my-listings`}
        element={
          <EmailVerificationGate>
            <ManageListingsPage
              properties={propertiesList}
              inquiries={inquiriesList}
              currentUser={currentUser}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onSwitchUser={(u) => setCurrentUser(u)}
            />
          </EmailVerificationGate>
        }
      />
      {/* Dynamic Marketplace Hierarchical Categories & Locations */}
      <Route
        path={`${prefix}/:categorySlug/*`}
        element={
          <MarketplaceCategoryLocationPage
            properties={propertiesList}
            isLoading={isLoadingProperties}
            onShowToast={showNotification}
          />
        }
      />
    </React.Fragment>
  );

  const renderFrenchRoutes = () => (
    <React.Fragment key="fr-routes">
      {/* Home */}
      <Route path="/fr" element={<HomePage properties={propertiesList} isLoading={isLoadingProperties} onShowToast={showNotification} />} />
      <Route path="/fr/" element={<HomePage properties={propertiesList} isLoading={isLoadingProperties} onShowToast={showNotification} />} />

      {/* For Sale */}
      <Route path="/fr/biens-a-vendre" element={<BuyPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/biens-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/terrains-a-vendre" element={<BuyPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />} />
      <Route path="/fr/terrains-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-a-vendre" element={<BuyPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-a-vendre" element={<BuyPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />} />
      <Route path="/fr/immobilier-commercial-a-vendre" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/immobilier-commercial-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/boutiques-et-bureaux-a-vendre" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/boutiques-et-bureaux-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/commercial-a-vendre" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/commercial-a-vendre/:citySlug" element={<BuyPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />

      {/* For Rent */}
      <Route path="/fr/biens-a-louer" element={<RentPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/biens-a-louer/:citySlug" element={<RentPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-a-louer" element={<RentPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="apartment" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-a-louer" element={<RentPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="house" isLoading={isLoadingProperties} />} />
      <Route path="/fr/studios-a-louer" element={<RentPage properties={propertiesList} forcedType="studio" isLoading={isLoadingProperties} />} />
      <Route path="/fr/studios-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="studio" isLoading={isLoadingProperties} />} />
      <Route path="/fr/chambres-a-louer" element={<RentPage properties={propertiesList} forcedType="room" isLoading={isLoadingProperties} />} />
      <Route path="/fr/chambres-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="room" isLoading={isLoadingProperties} />} />
      <Route path="/fr/terrains-a-louer" element={<RentPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />} />
      <Route path="/fr/terrains-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="land" isLoading={isLoadingProperties} />} />
      <Route path="/fr/locaux-commerciaux-a-louer" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/locaux-commerciaux-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/boutiques-et-bureaux-a-louer" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/boutiques-et-bureaux-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/commercial-a-louer" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />
      <Route path="/fr/commercial-a-louer/:citySlug" element={<RentPage properties={propertiesList} forcedType="commercial" isLoading={isLoadingProperties} />} />

      {/* Short Stays & Hotels */}
      <Route path="/fr/sejours-courts" element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/sejours-courts/:citySlug" element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/courts-sejours" element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/courts-sejours/:citySlug" element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />} />
      <Route path="/fr/hotels" element={<StayPage properties={propertiesList} forcedCategory="hotel" isLoading={isLoadingProperties} />} />
      <Route path="/fr/hotels/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="hotel" isLoading={isLoadingProperties} />} />
      <Route path="/fr/motels" element={<StayPage properties={propertiesList} forcedCategory="motel" isLoading={isLoadingProperties} />} />
      <Route path="/fr/motels/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="motel" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-dhotes" element={<StayPage properties={propertiesList} forcedCategory="guesthouse" isLoading={isLoadingProperties} />} />
      <Route path="/fr/maisons-dhotes/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="guesthouse" isLoading={isLoadingProperties} />} />
      <Route path="/fr/residences" element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />} />
      <Route path="/fr/residences/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />} />
      <Route path="/fr/complexes-hoteliers" element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />} />
      <Route path="/fr/complexes-hoteliers/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="resort" isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-meubles" element={<StayPage properties={propertiesList} forcedCategory="serviced" isLoading={isLoadingProperties} />} />
      <Route path="/fr/appartements-meubles/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="serviced" isLoading={isLoadingProperties} />} />
      <Route path="/fr/residences-meublees" element={<StayPage properties={propertiesList} forcedCategory="serviced" isLoading={isLoadingProperties} />} />
      <Route path="/fr/residences-meublees/:citySlug" element={<StayPage properties={propertiesList} forcedCategory="serviced" isLoading={isLoadingProperties} />} />

      {/* Property & Hotel Details */}
      <Route
        path="/fr/listing/:slug"
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route
        path="/fr/annonce/:slug"
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route
        path="/fr/propriete/:slug"
        element={
          <SafePropertyDetailPage
            properties={propertiesList}
            onDeleteProperty={handleDeleteProperty}
            onUpdateProperty={handleUpdateProperty}
          />
        }
      />
      <Route path="/fr/hotel/:slug" element={<HotelDetailPage properties={propertiesList} />} />

      {/* Interactive Map */}
      <Route path="/fr/carte" element={<SafeMapPage properties={propertiesList} isLoading={isLoadingProperties} />} />

      {/* Verify Property & Titre Foncier Guide */}
      <Route path="/fr/verifier-propriete" element={<VerifyPropertyPage />} />
      <Route path="/fr/verifier-titre-foncier" element={<VerifyTitreFoncierGuidePage />} />
      <Route path="/fr/verifier-titre-foncier/*" element={<VerifyTitreFoncierGuidePage />} />

      {/* Cities */}
      <Route path="/fr/villes" element={<CameroonCitiesPage />} />
      <Route path="/fr/villes/:citySlug" element={<CameroonCitiesPage />} />
      <Route path="/fr/ville/:citySlug" element={<CameroonCitiesPage />} />
      <Route path="/fr/city/:citySlug" element={<CameroonCitiesPage />} />
      <Route path="/villes" element={<CameroonCitiesPage />} />
      <Route path="/villes/:citySlug" element={<CameroonCitiesPage />} />

      {/* Regions */}
      <Route path="/fr/regions" element={<CameroonCitiesPage />} />
      <Route path="/fr/regions/:regionSlug" element={<RegionDetailPage />} />
      <Route path="/fr/region/:regionSlug" element={<RegionDetailPage />} />

      <Route path="/fr/sejours-courte-duree" element={<Navigate to="/fr/courts-sejours" replace />} />
      <Route path="/fr/sejours-courte-duree/:citySlug" element={<StayPage properties={propertiesList} isLoading={isLoadingProperties} />} />

      {/* Professionals */}
      <Route path="/fr/professionnels" element={<ProfessionalsPage />} />
      <Route path="/fr/professionnels/:category" element={<ProfessionalsPage />} />

      {/* Resources */}
      <Route path="/fr/ressources/frais-achat-terrain-et-taxes" element={<BuyingCostsGuidePage />} />
      <Route path="/fr/ressources/frais-achat-terrain-et-taxes/*" element={<BuyingCostsGuidePage />} />
      <Route path="/fr/ressources/abandon-coutumier-vs-titre-foncier" element={<CustomaryVsTitreFoncierGuidePage />} />
      <Route path="/fr/ressources/abandon-coutumier-vs-titre-foncier/*" element={<CustomaryVsTitreFoncierGuidePage />} />
      <Route path="/fr/ressources/trouver-geometre-assermente-ou-notaire" element={<FindSurveyorNotaryGuidePage />} />
      <Route path="/fr/ressources/trouver-geometre-assermente-ou-notaire/*" element={<FindSurveyorNotaryGuidePage />} />
      <Route path="/fr/ressources" element={<ResourcesPage />} />
      <Route path="/fr/ressources/:topicSlug" element={<ResourcesPage />} />

      {/* Post Property */}
      <Route
        path="/fr/deposer-annonce"
        element={
          <SafePostPropertyPage
            properties={propertiesList}
            onPropertyCreated={handlePropertyCreated}
          />
        }
      />
      <Route
        path="/fr/publier-annonce"
        element={
          <SafePostPropertyPage
            properties={propertiesList}
            onPropertyCreated={handlePropertyCreated}
          />
        }
      />

      {/* Static Information */}
      <Route path="/fr/actualites" element={<NewsPage />} />
      <Route path="/fr/actualites/:slug" element={<NewsPage />} />
      <Route path="/fr/news" element={<Navigate to="/fr/actualites" replace />} />
      <Route path="/fr/news/:slug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/a-propos" element={<AboutPage />} />
      <Route path="/fr/faq" element={<FaqPage />} />
      <Route path="/fr/contact" element={<ContactPage />} />
      <Route path="/fr/conditions-utilisation" element={<TermsPage />} />
      <Route path="/fr/conditions" element={<TermsPage />} />
      <Route path="/fr/politique-confidentialite" element={<PrivacyPage />} />
      <Route path="/fr/confidentialite" element={<PrivacyPage />} />
      <Route path="/fr/plan-du-site" element={<SitemapPage />} />
      <Route path="/fr/sitemap" element={<Navigate to="/fr/plan-du-site" replace />} />
      <Route path="/fr/html-sitemap" element={<Navigate to="/fr/plan-du-site" replace />} />

      {/* Auth & Profile */}
      <Route path="/fr/connexion" element={<SignInPage onLoginSuccess={(user) => setCurrentUser(user)} />} />
      <Route path="/fr/inscription" element={<SignUpPage onLoginSuccess={(user) => setCurrentUser(user)} />} />
      <Route path="/fr/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
      <Route path="/fr/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
      <Route path="/fr/mon-profil" element={<ProfilePage />} />
      <Route path="/fr/profile" element={<Navigate to="/fr/mon-profil" replace />} />
      <Route path="/fr/profile/*" element={<Navigate to="/fr/mon-profil" replace />} />
      <Route path="/fr/forgot-password" element={<Navigate to="/fr/mot-de-passe-oublie" replace />} />
      <Route path="/fr/reset-password" element={<Navigate to="/fr/reinitialiser-mot-de-passe" replace />} />
      <Route
        path="/fr/mes-annonces"
        element={
          <EmailVerificationGate>
            <ManageListingsPage
              properties={propertiesList}
              inquiries={inquiriesList}
              currentUser={currentUser}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onSwitchUser={(u) => setCurrentUser(u)}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path="/fr/tableau-de-bord"
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />
      <Route
        path="/fr/statistiques-vendeur"
        element={
          <EmailVerificationGate>
            <UserAccountDashboardPage
              properties={propertiesList}
              inquiries={inquiriesList}
              onUpdateProperty={handleUpdateProperty}
              onDeleteProperty={handleDeleteProperty}
              onUpdateInquiryStatus={handleUpdateInquiryStatus}
              onDeleteInquiry={handleDeleteInquiry}
            />
          </EmailVerificationGate>
        }
      />

      {/* 301 Permanent Redirects for English route aliases under /fr to prevent duplicate content */}
      <Route path="/fr/property/:slug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/map" element={<Navigate to="/fr/carte" replace />} />
      <Route path="/fr/verify-property" element={<Navigate to="/fr/verifier-propriete" replace />} />
      <Route path="/fr/properties-for-sale" element={<Navigate to="/fr/biens-a-vendre" replace />} />
      <Route path="/fr/properties-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/land-for-sale" element={<Navigate to="/fr/terrains-a-vendre" replace />} />
      <Route path="/fr/land-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/houses-for-sale" element={<Navigate to="/fr/maisons-a-vendre" replace />} />
      <Route path="/fr/houses-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/apartments-for-sale" element={<Navigate to="/fr/appartements-a-vendre" replace />} />
      <Route path="/fr/apartments-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/commercial-for-sale" element={<Navigate to="/fr/commercial-a-vendre" replace />} />
      <Route path="/fr/commercial-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/shops-and-offices-for-sale" element={<Navigate to="/fr/boutiques-et-bureaux-a-vendre" replace />} />
      <Route path="/fr/shops-and-offices-for-sale/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/properties-for-rent" element={<Navigate to="/fr/biens-a-louer" replace />} />
      <Route path="/fr/properties-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/apartments-for-rent" element={<Navigate to="/fr/appartements-a-louer" replace />} />
      <Route path="/fr/apartments-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/houses-for-rent" element={<Navigate to="/fr/maisons-a-louer" replace />} />
      <Route path="/fr/houses-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/studios-for-rent" element={<Navigate to="/fr/studios-a-louer" replace />} />
      <Route path="/fr/studios-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/rooms-for-rent" element={<Navigate to="/fr/chambres-a-louer" replace />} />
      <Route path="/fr/rooms-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/land-for-rent" element={<Navigate to="/fr/terrains-a-louer" replace />} />
      <Route path="/fr/land-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/commercial-for-rent" element={<Navigate to="/fr/commercial-a-louer" replace />} />
      <Route path="/fr/commercial-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/shops-and-offices-for-rent" element={<Navigate to="/fr/boutiques-et-bureaux-a-louer" replace />} />
      <Route path="/fr/shops-and-offices-for-rent/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/short-stays" element={<Navigate to="/fr/sejours-courts" replace />} />
      <Route path="/fr/short-stays/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/cities" element={<Navigate to="/fr/villes" replace />} />
      <Route path="/fr/cities/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/professionals" element={<Navigate to="/fr/professionnels" replace />} />
      <Route path="/fr/professionals/:category" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/resources" element={<Navigate to="/fr/ressources" replace />} />
      <Route path="/fr/resources/:topicSlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/about" element={<Navigate to="/fr/a-propos" replace />} />
      <Route path="/fr/terms" element={<Navigate to="/fr/conditions-utilisation" replace />} />
      <Route path="/fr/privacy" element={<Navigate to="/fr/confidentialite" replace />} />
      <Route path="/fr/signin" element={<Navigate to="/fr/connexion" replace />} />
      <Route path="/fr/sign-in" element={<Navigate to="/fr/connexion" replace />} />
      <Route path="/fr/signup" element={<Navigate to="/fr/inscription" replace />} />
      <Route path="/fr/sign-up" element={<Navigate to="/fr/inscription" replace />} />
      <Route path="/fr/renew/:propertyId" element={<RenewListingPage properties={propertiesList} onUpdateProperty={handleUpdateProperty} />} />
      <Route path="/fr/renouveler/:propertyId" element={<RenewListingPage properties={propertiesList} onUpdateProperty={handleUpdateProperty} />} />
      <Route path="/renew/:propertyId" element={<RenewListingPage properties={propertiesList} onUpdateProperty={handleUpdateProperty} />} />
      <Route path="/fr/dashboard" element={<Navigate to="/fr/tableau-de-bord" replace />} />
      <Route path="/fr/dashboard/*" element={<Navigate to="/fr/tableau-de-bord" replace />} />
      <Route path="/fr/manage-listings" element={<Navigate to="/fr/mes-annonces" replace />} />
      <Route path="/fr/manage-listings/*" element={<Navigate to="/fr/mes-annonces" replace />} />
      <Route path="/fr/my-listings" element={<Navigate to="/fr/mes-annonces" replace />} />
      <Route path="/fr/my-listings/*" element={<Navigate to="/fr/mes-annonces" replace />} />
      <Route path="/fr/post" element={<Navigate to="/fr/deposer-annonce" replace />} />
      <Route path="/fr/post-property" element={<Navigate to="/fr/deposer-annonce" replace />} />
      <Route path="/fr/guesthouses" element={<Navigate to="/fr/maisons-dhotes" replace />} />
      <Route path="/fr/guesthouses/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/guest-houses" element={<Navigate to="/fr/maisons-dhotes" replace />} />
      <Route path="/fr/guest-houses/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/resorts" element={<Navigate to="/fr/residences" replace />} />
      <Route path="/fr/resorts/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/serviced-apartments" element={<Navigate to="/fr/appartements-meubles" replace />} />
      <Route path="/fr/serviced-apartments/:citySlug" element={<RedirectToFrenchRoute />} />
      <Route path="/fr/verify-titre-foncier" element={<Navigate to="/fr/verifier-titre-foncier" replace />} />
      {/* Dynamic Marketplace Hierarchical Categories & Locations (French) */}
      <Route
        path="/fr/:categorySlug/*"
        element={
          <MarketplaceCategoryLocationPage
            properties={propertiesList}
            isLoading={isLoadingProperties}
            onShowToast={showNotification}
          />
        }
      />
    </React.Fragment>
  );

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-white text-slate-900">
        {/* Toast alert banner */}
        {toastMessage && (
          <div className="fixed bottom-6 right-6 z-50 bg-[#094426] text-white px-4 py-3 rounded-2xl shadow-xl border border-emerald-400/40 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-200 max-w-md text-xs">
            <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" />
            <span className="font-semibold flex-1">{toastMessage}</span>
            <button
              type="button"
              onClick={() => setToastMessage(null)}
              className="text-white/70 hover:text-white p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Offline Connectivity Notification Banner */}
        {!isAdminRoute && <OfflineBanner />}

        {/* Global Header with active route detection & dropdowns - Hidden on private /admin platform */}
        {!isAdminRoute && (
          <ErrorBoundary
            fallbackTitle="En-tête • Rechargement requis"
            fallbackSubtitle="L'en-tête de navigation a rencontré une interruption temporaire."
            homeUrl="/"
            homeLabel="Accueil / Home"
          >
            <Header
              currentUser={currentUser}
              onSignOut={async () => {
                try {
                  await authSignOut();
                } catch {}
                setCurrentUser(null);
                setInquiriesList([]);
                showNotification('Signed out successfully.');
              }}
              onNotificationClick={(item) => {
                showNotification(`Notification: ${item.title}`);
              }}
            />
          </ErrorBoundary>
        )}

        {/* Main Content View with In-Page Navigation */}
        <main className="flex-1">
          <ErrorBoundary
            homeUrl="/"
            homeLabel="Accueil / Home"
          >
            <React.Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
              {/* 1. Explicit French Localized Routes FIRST */}
              {renderFrenchRoutes()}

              {/* 2. Explicit English Localized Routes SECOND */}
              {renderCoreRoutes('/en')}

              {/* ============================================================== */}
              {/* PRIVATE & SECURE ADMIN APPLICATION AT /admin                   */}
              {/* Strictly separate from public marketplace. RBAC & 2FA Protected*/}
              {/* ============================================================== */}
              <Route path="/admin/login" element={<AdminLoginPageNew />} />
              <Route path="/en/admin/login" element={<Navigate to="/admin/login" replace />} />
              <Route path="/fr/admin/login" element={<Navigate to="/admin/login" replace />} />

              <Route
                path="/admin/*"
                element={
                  <ErrorBoundary fallbackTitle="Console Administrative • Restauration Sécurisée" fallbackSubtitle="Une interruption est survenue lors de l'initialisation de l'espace d'administration. Cliquez pour recharger l'interface.">
                    <AdminProtectedRoute>
                      {(adminUser) => (
                        <AdminPlatformPage
                          currentAdmin={adminUser}
                          properties={propertiesList}
                          inquiries={inquiriesList}
                          onUpdateProperty={handleUpdateProperty}
                          onDeleteProperty={handleDeleteProperty}
                          onUpdateInquiryStatus={handleUpdateInquiryStatus}
                          onDeleteInquiry={handleDeleteInquiry}
                        />
                      )}
                    </AdminProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin"
                element={
                  <ErrorBoundary fallbackTitle="Console Administrative • Restauration Sécurisée" fallbackSubtitle="Une interruption est survenue lors de l'initialisation de l'espace d'administration. Cliquez pour recharger l'interface.">
                    <AdminProtectedRoute>
                      {(adminUser) => (
                        <AdminPlatformPage
                          currentAdmin={adminUser}
                          properties={propertiesList}
                          inquiries={inquiriesList}
                          onUpdateProperty={handleUpdateProperty}
                          onDeleteProperty={handleDeleteProperty}
                          onUpdateInquiryStatus={handleUpdateInquiryStatus}
                          onDeleteInquiry={handleDeleteInquiry}
                        />
                      )}
                    </AdminProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route path="/en/admin/*" element={<Navigate to="/admin" replace />} />
              <Route path="/en/admin" element={<Navigate to="/admin" replace />} />
              <Route path="/fr/admin/*" element={<Navigate to="/admin" replace />} />
              <Route path="/fr/admin" element={<Navigate to="/admin" replace />} />

              {/* Seamless redirect from legacy secret vault to /admin */}
              <Route path="/clm-master-vault-9820/login" element={<Navigate to="/admin/login" replace />} />
              <Route path="/en/clm-master-vault-9820/login" element={<Navigate to="/admin/login" replace />} />
              <Route path="/fr/clm-master-vault-9820/login" element={<Navigate to="/admin/login" replace />} />
              <Route path="/clm-master-vault-9820" element={<Navigate to="/admin" replace />} />
              <Route path="/clm-master-vault-9820/*" element={<Navigate to="/admin" replace />} />
              <Route path="/en/clm-master-vault-9820" element={<Navigate to="/admin" replace />} />
              <Route path="/en/clm-master-vault-9820/*" element={<Navigate to="/admin" replace />} />
              <Route path="/fr/clm-master-vault-9820" element={<Navigate to="/admin" replace />} />
              <Route path="/fr/clm-master-vault-9820/*" element={<Navigate to="/admin" replace />} />

              <Route
                path="/__/auth/action"
                element={<ResetPasswordPage />}
              />
              <Route
                path="/auth/action"
                element={<ResetPasswordPage />}
              />

              {/* 3. Friendly Aliases & SEO Redirects (Evaluated BEFORE category wildcards) */}
              <Route path="/blog/cost-of-buying-land-cameroon" element={<Navigate to="/en/resources/cost-of-buying-land-and-taxes" replace />} />
              <Route path="/blog/frais-achat-terrain-cameroun" element={<Navigate to="/fr/ressources/frais-achat-terrain-et-taxes" replace />} />
              <Route path="/cost-of-buying-land-and-taxes" element={<Navigate to="/en/resources/cost-of-buying-land-and-taxes" replace />} />
              <Route path="/frais-achat-terrain-et-taxes" element={<Navigate to="/fr/ressources/frais-achat-terrain-et-taxes" replace />} />
              <Route path="/resources/cost-of-buying-land-and-taxes" element={<Navigate to="/en/resources/cost-of-buying-land-and-taxes" replace />} />
              <Route path="/ressources/frais-achat-terrain-et-taxes" element={<Navigate to="/fr/ressources/frais-achat-terrain-et-taxes" replace />} />
              <Route path="/customary-land-vs-titre-foncier" element={<Navigate to="/en/resources/customary-land-vs-titre-foncier" replace />} />
              <Route path="/abandon-coutumier-vs-titre-foncier" element={<Navigate to="/fr/ressources/abandon-coutumier-vs-titre-foncier" replace />} />
              <Route path="/resources/customary-land-vs-titre-foncier" element={<Navigate to="/en/resources/customary-land-vs-titre-foncier" replace />} />
              <Route path="/ressources/abandon-coutumier-vs-titre-foncier" element={<Navigate to="/fr/ressources/abandon-coutumier-vs-titre-foncier" replace />} />
              <Route path="/how-to-find-licensed-surveyor-or-notary" element={<Navigate to="/en/resources/how-to-find-licensed-surveyor-or-notary" replace />} />
              <Route path="/trouver-geometre-assermente-ou-notaire" element={<Navigate to="/fr/ressources/trouver-geometre-assermente-ou-notaire" replace />} />
              <Route path="/resources/how-to-find-licensed-surveyor-or-notary" element={<Navigate to="/en/resources/how-to-find-licensed-surveyor-or-notary" replace />} />
              <Route path="/ressources/trouver-geometre-assermente-ou-notaire" element={<Navigate to="/fr/ressources/trouver-geometre-assermente-ou-notaire" replace />} />
              <Route path="/blog/customary-land-vs-titre-foncier-cameroon" element={<Navigate to="/en/resources/customary-land-vs-titre-foncier" replace />} />
              <Route path="/blog/abandon-coutumier-cameroun" element={<Navigate to="/fr/ressources/abandon-coutumier-vs-titre-foncier" replace />} />
              <Route path="/blog/how-to-check-titre-foncier-cameroon" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/blog/how-to-check-titre-foncier" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/blog" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/blog/*" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/en/blog" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/en/blog/*" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/fr/blog" element={<Navigate to="/fr/verifier-titre-foncier" replace />} />
              <Route path="/fr/blog/*" element={<Navigate to="/fr/verifier-titre-foncier" replace />} />
              <Route path="/verify-titre-foncier" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/verify-titre-foncier/*" element={<Navigate to="/en/verify-titre-foncier" replace />} />
              <Route path="/hotel" element={<Navigate to="/hotels" replace />} />
              <Route path="/short-stay" element={<Navigate to="/short-stays" replace />} />
              <Route path="/rentals" element={<Navigate to="/properties-for-rent" replace />} />
              <Route path="/for-sale" element={<Navigate to="/properties-for-sale" replace />} />
              <Route path="/buy" element={<Navigate to="/properties-for-sale" replace />} />
              <Route path="/sell" element={<Navigate to="/en/post-property" replace />} />
              <Route path="/acheter" element={<Navigate to="/fr/biens-a-vendre" replace />} />
              <Route path="/vendre" element={<Navigate to="/fr/deposer-annonce" replace />} />
              <Route path="/listings" element={<Navigate to="/properties-for-sale" replace />} />
              <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
              <Route path="/about-us" element={<Navigate to="/about" replace />} />
              <Route path="/help" element={<Navigate to="/faq" replace />} />
              <Route path="/login" element={<Navigate to="/signin" replace />} />
              <Route path="/register" element={<Navigate to="/signup" replace />} />
              <Route path="/city/:citySlug" element={<CameroonCitiesPage />} />
              <Route path="/ville/:citySlug" element={<CameroonCitiesPage />} />
              <Route path="/sejours-courte-duree" element={<Navigate to="/short-stays" replace />} />
              <Route path="/apartments" element={<Navigate to="/apartments-for-sale" replace />} />
              <Route path="/houses" element={<Navigate to="/houses-for-sale" replace />} />
              <Route path="/land" element={<Navigate to="/land-for-sale" replace />} />
              <Route path="/plan-du-site" element={<Navigate to="/fr/plan-du-site" replace />} />

              {/* 4. Core Root & Unprefixed Routes */}
              {renderCoreRoutes('')}

              {/* Branded 404 Not Found Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </React.Suspense>
          </ErrorBoundary>
        </main>

        {/* Comparison Floating Drawer & Side-by-Side Modal */}
        {!isAdminRoute && (
          <>
            <CompareFloatingBar />
            <React.Suspense fallback={null}>
              <CompareModal />
              <PropertyAlertModal />
            </React.Suspense>
          </>
        )}

        {/* Global Footer with real page routing links - Hidden on private /admin platform */}
        {!isAdminRoute && <Footer properties={propertiesList} />}

        {/* Ergonomic Mobile Bottom App Navigation Bar (PWA & Native App) */}
        {!isAdminRoute && <MobileBottomNav />}

        {/* 1-Tap Mobile App Install Banner (Android Chrome & iOS Safari) */}
        {!isAdminRoute && <InstallAppBanner />}
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* <LanguageRouteSync /> -- Commented out per Req 4 to prevent language flip-flop loop */}
      <LegacyRedirectHandler />
      <AuthProvider>
        <CurrencyProvider>
          <CompareProvider>
            <PropertyAlertProvider>
              <AppContent />
            </PropertyAlertProvider>
          </CompareProvider>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
